import { BACKGROUND_COLORS, TEXT_COLORS, normalizeColor } from "./formattingConfig.mjs";
import { getEditorSelectionAnalysis } from "./editorSelectionAnalysis.mjs";

export const COMMAND_CONTEXTS = Object.freeze({
  BLOCK: "block",
  SELECTION: "selection",
  SLASH: "slash"
});

const BLOCK = COMMAND_CONTEXTS.BLOCK;
const SELECTION = COMMAND_CONTEXTS.SELECTION;
const SLASH = COMMAND_CONTEXTS.SLASH;
const INVALID_PAYLOAD = Symbol("invalid-payload");
const CAPABILITY_CACHE = new WeakMap();
const HEADING_LEVEL_NAMES = Object.freeze(["一", "二", "三", "四", "五"]);

function selectedMarkState(editor, markName, attrs = {}) {
  const analysis = getEditorSelectionAnalysis(editor);
  const active = markName === "link" && Object.keys(attrs).length === 0
    ? analysis?.linkState()
    : analysis?.markState(markName, attrs);
  return active === undefined ? editor.isActive(markName, attrs) : active;
}

function selectedStructuralState(editor, nodeName, attrs = {}) {
  const active = getEditorSelectionAnalysis(editor)?.structuralState(nodeName, attrs);
  return active === undefined ? editor.isActive(nodeName, attrs) : active;
}

function selectedAlignmentState(editor, alignment) {
  const active = getEditorSelectionAnalysis(editor)?.alignmentState(alignment);
  return active === undefined ? editor.isActive({ textAlign: alignment }) : active;
}

function ordinaryIndentCanChange(editor, increase) {
  return getEditorSelectionAnalysis(editor)?.indentCanChange(increase) ?? true;
}

function resetColorsCanExecute(editor) {
  const availability = getEditorSelectionAnalysis(editor)?.hasColorFormatting;
  return availability === undefined
    ? editor.isActive("textStyle") || editor.isActive("highlight")
    : availability;
}

function defineCommand(command) {
  return Object.freeze({
    kind: "editor",
    ...command,
    shortLabel: command.shortLabel ?? command.label,
    contexts: Object.freeze([...command.contexts]),
    aliases: Object.freeze([...command.aliases])
  });
}

function structuralCommand({ id, label, shortLabel, aliases, node, contexts = [BLOCK, SELECTION, SLASH], apply }) {
  return defineCommand({
    id,
    label,
    shortLabel,
    aliases,
    contexts,
    isActive: (editor) => selectedStructuralState(editor, node),
    apply
  });
}

function headingCommand(level) {
  return defineCommand({
    id: `heading-${level}`,
    label: `${level} 级标题`,
    shortLabel: `H${level}`,
    aliases: [
      `h${level}`,
      `heading ${level}`,
      `${level}级标题`,
      `${level} 级标题`,
      `${HEADING_LEVEL_NAMES[level - 1]}级标题`
    ],
    contexts: [BLOCK, SELECTION, SLASH],
    isActive: (editor) => selectedStructuralState(editor, "heading", { level }),
    apply: (chain) => chain.setHeading({ level })
  });
}

function alignmentCommand(alignment, label, aliases) {
  return defineCommand({
    id: `align-${alignment}`,
    label,
    aliases,
    contexts: [BLOCK, SELECTION],
    canExecute: (editor) => getEditorSelectionAnalysis(editor)?.hasAlignable ?? true,
    isActive: (editor) => selectedAlignmentState(editor, alignment),
    apply: (chain) => chain.setTextAlign(alignment)
  });
}

function markCommand(id, label, aliases, mark, method) {
  return defineCommand({
    id,
    label,
    aliases,
    contexts: [SELECTION],
    isActive: (editor) => selectedMarkState(editor, mark),
    apply: (chain) => chain[method]()
  });
}

function colorCommand({ id, label, aliases, palette, mark, apply }) {
  return defineCommand({
    id,
    label,
    aliases,
    contexts: [BLOCK, SELECTION],
    validate: (payload) => normalizeColor(payload?.color, palette) !== null,
    capabilityKey: (payload) => normalizeColor(payload?.color, palette) ?? INVALID_PAYLOAD,
    isActive: (editor, payload) => {
      const color = normalizeColor(payload?.color, palette);
      return color === null ? false : selectedMarkState(editor, mark, { color });
    },
    apply: (chain, payload) => apply(chain, normalizeColor(payload?.color, palette))
  });
}

function normalizeLinkPayload(payload) {
  const href = payload?.href;
  if (href === undefined || href === null) return { unset: true };
  if (typeof href !== "string") return INVALID_PAYLOAD;
  const trimmed = href.trim();
  if (!trimmed) return { unset: true };
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "http:" && url.protocol !== "https:") return INVALID_PAYLOAD;
    return { href: trimmed };
  } catch {
    return INVALID_PAYLOAD;
  }
}

function activeListItemType(editor) {
  const analysis = getEditorSelectionAnalysis(editor);
  if (analysis) {
    return analysis.listItemType;
  }
  if (editor.isActive("taskItem")) return "taskItem";
  if (editor.isActive("listItem")) return "listItem";
  return null;
}

function indentCommand(direction) {
  const increase = direction === "increase";
  return defineCommand({
    id: `indent-${direction}`,
    label: increase ? "增加缩进" : "减少缩进",
    aliases: increase ? ["indent", "increase indent", "增加缩进"] : ["outdent", "decrease indent", "减少缩进"],
    contexts: [BLOCK, SELECTION],
    canExecute: (editor) => ordinaryIndentCanChange(editor, increase),
    apply: (chain, _payload, editor) => {
      const listItemType = activeListItemType(editor);
      if (listItemType) return increase ? chain.sinkListItem(listItemType) : chain.liftListItem(listItemType);
      return increase ? chain.increaseIndent() : chain.decreaseIndent();
    }
  });
}

const COMMANDS = Object.freeze([
  structuralCommand({
    id: "paragraph",
    label: "正文",
    aliases: ["paragraph", "text", "正文", "文本"],
    node: "paragraph",
    apply: (chain) => chain.setParagraph()
  }),
  headingCommand(1),
  headingCommand(2),
  headingCommand(3),
  headingCommand(4),
  headingCommand(5),
  structuralCommand({
    id: "ordered-list",
    label: "有序列表",
    aliases: ["ordered list", "numbered list", "ol", "有序列表", "编号列表"],
    node: "orderedList",
    apply: (chain) => chain.toggleOrderedList()
  }),
  structuralCommand({
    id: "bullet-list",
    label: "无序列表",
    aliases: ["bullet list", "unordered list", "ul", "无序列表", "项目符号"],
    node: "bulletList",
    apply: (chain) => chain.toggleBulletList()
  }),
  structuralCommand({
    id: "task-list",
    label: "任务列表",
    aliases: ["task list", "checklist", "todo", "任务列表", "待办列表"],
    node: "taskList",
    apply: (chain) => chain.toggleTaskList()
  }),
  structuralCommand({
    id: "code-block",
    label: "代码块",
    aliases: ["code block", "code", "代码块", "代码"],
    node: "codeBlock",
    apply: (chain) => chain.toggleCodeBlock()
  }),
  structuralCommand({
    id: "blockquote",
    label: "引用",
    aliases: ["blockquote", "quote", "引用", "引述"],
    node: "blockquote",
    apply: (chain) => chain.toggleBlockquote()
  }),
  defineCommand({
    id: "horizontal-rule",
    label: "分隔线",
    aliases: ["horizontal rule", "divider", "separator", "分隔线", "水平线"],
    contexts: [BLOCK, SLASH],
    apply: (chain) => chain.setHorizontalRule()
  }),
  alignmentCommand("left", "左对齐", ["align left", "left", "左对齐"]),
  alignmentCommand("center", "居中对齐", ["align center", "center", "居中", "居中对齐"]),
  alignmentCommand("right", "右对齐", ["align right", "right", "右对齐"]),
  indentCommand("increase"),
  indentCommand("decrease"),
  markCommand("bold", "粗体", ["bold", "粗体", "加粗"], "bold", "toggleBold"),
  markCommand("strike", "删除线", ["strike", "strikethrough", "删除线"], "strike", "toggleStrike"),
  markCommand("italic", "斜体", ["italic", "斜体"], "italic", "toggleItalic"),
  markCommand("underline", "下划线", ["underline", "下划线"], "underline", "toggleUnderline"),
  defineCommand({
    id: "link",
    label: "链接",
    aliases: ["link", "url", "链接", "超链接"],
    contexts: [SELECTION],
    validate: (payload) => normalizeLinkPayload(payload) !== INVALID_PAYLOAD,
    capabilityKey: (payload) => {
      const link = normalizeLinkPayload(payload);
      if (link === INVALID_PAYLOAD) return INVALID_PAYLOAD;
      return link.unset ? "unset" : `href:${link.href}`;
    },
    isActive: (editor) => selectedMarkState(editor, "link"),
    apply: (chain, payload) => {
      const link = normalizeLinkPayload(payload);
      return link.unset ? chain.unsetLink() : chain.setLink({ href: link.href });
    }
  }),
  markCommand("inline-code", "行内代码", ["inline code", "code", "行内代码"], "code", "toggleCode"),
  colorCommand({
    id: "text-color",
    label: "文字颜色",
    aliases: ["text color", "font color", "文字颜色", "字体颜色"],
    palette: TEXT_COLORS,
    mark: "textStyle",
    apply: (chain, color) => chain.setColor(color)
  }),
  colorCommand({
    id: "background-color",
    label: "背景颜色",
    aliases: ["background color", "highlight color", "背景颜色", "高亮颜色"],
    palette: BACKGROUND_COLORS,
    mark: "highlight",
    apply: (chain, color) => chain.setHighlight({ color })
  }),
  defineCommand({
    id: "reset-colors",
    label: "恢复默认颜色",
    aliases: ["reset colors", "default colors", "恢复颜色", "默认颜色"],
    contexts: [BLOCK, SELECTION],
    canExecute: resetColorsCanExecute,
    canMode: "snapshot",
    apply: (chain) => chain.unsetColor().unsetHighlight()
  }),
  defineCommand({
    id: "clear-formatting",
    label: "清除格式",
    aliases: ["clear formatting", "clear", "清除格式"],
    contexts: [SELECTION],
    apply: (chain) => chain.unsetAllMarks().clearNodes()
  }),
  defineCommand({
    id: "ai",
    label: "AI",
    shortLabel: "AI",
    icon: "sparkles",
    aliases: ["ai", "artificial intelligence", "人工智能", "智能写作"],
    contexts: [SELECTION],
    kind: "service"
  }),
  defineCommand({
    id: "block-cut",
    label: "剪切块",
    aliases: ["cut block", "cut", "剪切块", "剪切"],
    contexts: [BLOCK],
    kind: "service"
  }),
  defineCommand({
    id: "block-copy",
    label: "复制块",
    aliases: ["copy block", "copy", "复制块", "复制"],
    contexts: [BLOCK],
    kind: "service"
  }),
  defineCommand({
    id: "block-delete",
    label: "删除块",
    aliases: ["delete block", "delete", "删除块", "删除"],
    contexts: [BLOCK],
    kind: "service"
  }),
  defineCommand({ id: "undo", label: "撤销", aliases: ["undo", "撤销"], contexts: [], apply: (chain) => chain.undo() }),
  defineCommand({ id: "redo", label: "重做", aliases: ["redo", "重做"], contexts: [], apply: (chain) => chain.redo() })
]);

const LEGACY_HIGHLIGHT = defineCommand({
  id: "highlight",
  label: "高亮",
  aliases: ["highlight", "高亮"],
  contexts: [],
  isActive: (editor) => selectedMarkState(editor, "highlight"),
  apply: (chain) => chain.toggleHighlight()
});

const COMMAND_BY_ID = new Map(COMMANDS.map((command) => [command.id, command]));
COMMAND_BY_ID.set("quote", COMMAND_BY_ID.get("blockquote"));
COMMAND_BY_ID.set("ul", COMMAND_BY_ID.get("bullet-list"));
COMMAND_BY_ID.set("ol", COMMAND_BY_ID.get("ordered-list"));
COMMAND_BY_ID.set("clear", COMMAND_BY_ID.get("clear-formatting"));
COMMAND_BY_ID.set("highlight", LEGACY_HIGHLIGHT);

export function getCommandsForContext(context) {
  return COMMANDS.filter((command) => command.contexts.includes(context));
}

function probeCapability(editor, command, payload) {
  if (command.kind === "service") return true;
  if (command.canExecute && command.canExecute(editor, payload) !== true) return false;
  if (command.canMode === "snapshot") return true;
  if (typeof editor?.can !== "function") return typeof editor?.chain === "function";
  const can = editor.can();
  if (typeof can?.chain !== "function") return false;
  const chain = can.chain().focus();
  return command.apply(chain, payload, editor).run() === true;
}

function canExecute(editor, command, payload, useCache = true) {
  try {
    const cacheableEditor = (typeof editor === "object" && editor !== null) || typeof editor === "function";
    const editable = editor?.isEditable;
    if (editable === false) {
      if (useCache && cacheableEditor) {
        const state = editor.state;
        const entry = CAPABILITY_CACHE.get(editor);
        if (!entry || entry.state !== state || entry.editable !== editable) {
          CAPABILITY_CACHE.set(editor, { state, editable, commands: new Map() });
        }
      }
      return false;
    }
    if (command.validate && command.validate(payload) !== true) return false;
    const payloadKey = command.capabilityKey ? command.capabilityKey(payload) : "";
    if (payloadKey === INVALID_PAYLOAD) return false;
    if (!useCache || !cacheableEditor) return probeCapability(editor, command, payload);

    const state = editor.state;
    let entry = CAPABILITY_CACHE.get(editor);
    if (!entry || entry.state !== state || entry.editable !== editable) {
      entry = { state, editable, commands: new Map() };
      CAPABILITY_CACHE.set(editor, entry);
    }
    let commandCache = entry.commands.get(command.id);
    if (!commandCache) {
      commandCache = new Map();
      entry.commands.set(command.id, commandCache);
    }
    if (commandCache.has(payloadKey)) return commandCache.get(payloadKey);
    const enabled = probeCapability(editor, command, payload);
    commandCache.set(payloadKey, enabled);
    return enabled;
  } catch {
    return false;
  }
}

export function getEditorCommandState(editor, id, payload = {}) {
  const command = COMMAND_BY_ID.get(id);
  if (!command) return { active: false, enabled: false };

  let active = false;
  try {
    if (command.isActive) active = command.isActive(editor, payload);
  } catch {
    active = false;
  }
  return { active, enabled: canExecute(editor, command, payload) };
}

export function prepareEditorCommand(editor, id, payload = {}, context) {
  try {
    const command = COMMAND_BY_ID.get(id);
    if (!command
      || command.kind === "service"
      || (context !== undefined && (command.id !== id || !command.contexts.includes(context)))) return null;

    if (context === SLASH && command.isActive) {
      if (editor?.isEditable === false) return null;
      let active = false;
      try {
        active = command.isActive(editor, payload) === true;
      } catch {
        active = false;
      }
      if (active) return (chain) => chain;
    }
    if (!canExecute(editor, command, payload, false)) return null;

    return (chain) => {
      try {
        return command.apply(chain, payload, editor);
      } catch {
        return null;
      }
    };
  } catch {
    return null;
  }
}

export function executeEditorCommand(editor, id, payload = {}) {
  const command = COMMAND_BY_ID.get(id);
  if (!command) return false;
  try {
    if (editor?.isEditable === false) return false;
  } catch {
    return false;
  }
  if (command.kind === "service" || !canExecute(editor, command, payload, false)) return false;
  try {
    const chain = editor.chain().focus();
    return command.apply(chain, payload, editor).run() === true;
  } catch {
    return false;
  }
}
